// Pre-Approved Reps Service
// Handles pre-approved sales rep relationships for auto-confirming meetings

import { supabase } from './supabase';
import { logger } from './logger';

export const preApprovedRepsService = {
  /**
   * Get all sales reps pre-approved by a customer
   * @param {string} customerId - The customer's UUID
   * @returns {Promise<Array>} Array of pre-approved sales rep objects
   */
  async getPreApprovedReps(customerId) {
    try {
      logger.debug('PreApprovedReps: Getting pre-approved reps for customer', { customerId });

      const { data, error } = await supabase
        .from('pre_approved_reps')
        .select(`
          id,
          sales_rep_id,
          approved_at,
          notes,
          sales_rep:user_profiles!sales_rep_id (
            id,
            first_name,
            last_name,
            email,
            company,
            phone
          )
        `)
        .eq('customer_id', customerId)
        .order('approved_at', { ascending: false });

      if (error) throw error;

      logger.debug('PreApprovedReps: Found pre-approved reps', { count: data?.length || 0 });

      // Transform the data to flatten the nested sales_rep object
      const transformed = (data || []).map(item => ({
        id: item.id,
        salesRepId: item.sales_rep_id,
        salesRepName: item.sales_rep ? `${item.sales_rep.first_name || ''} ${item.sales_rep.last_name || ''}`.trim() : 'Unknown',
        salesRepEmail: item.sales_rep?.email || '',
        salesRepCompany: item.sales_rep?.company || 'Unknown Company',
        salesRepPhone: item.sales_rep?.phone || '',
        approvedAt: item.approved_at,
        notes: item.notes
      }));

      return transformed;
    } catch (error) {
      logger.error('PreApprovedReps: Error getting pre-approved reps', { error: error.message, customerId });
      return [];
    }
  },

  /**
   * Get all customers who pre-approved a sales rep
   * @param {string} salesRepId - The sales rep's UUID
   * @returns {Promise<Array>} Array of customers who pre-approved this rep
   */
  async getCustomersWhoPreApproved(salesRepId) {
    try {
      logger.debug('PreApprovedReps: Getting customers who pre-approved rep', { salesRepId });

      const { data, error } = await supabase
        .from('pre_approved_reps')
        .select(`
          id,
          customer_id,
          approved_at,
          notes,
          customer:user_profiles!customer_id (
            id,
            first_name,
            last_name,
            email,
            specialty,
            company,
            phone
          )
        `)
        .eq('sales_rep_id', salesRepId)
        .order('approved_at', { ascending: false });

      if (error) throw error;

      logger.debug('PreApprovedReps: Found customers', { count: data?.length || 0 });

      // Transform the data to flatten the nested customer object
      const transformed = (data || []).map(item => ({
        id: item.id,
        customerId: item.customer_id,
        customerName: item.customer ? `${item.customer.first_name || ''} ${item.customer.last_name || ''}`.trim() : 'Unknown',
        customerEmail: item.customer?.email || '',
        customerCompany: item.customer?.company || item.customer?.specialty || 'Healthcare Provider',
        customerPhone: item.customer?.phone || '',
        customerSpecialty: item.customer?.specialty || '',
        approvedAt: item.approved_at,
        notes: item.notes
      }));

      return transformed;
    } catch (error) {
      logger.error('PreApprovedReps: Error getting customers', { error: error.message, salesRepId });
      return [];
    }
  },

  /**
   * Check if a sales rep is pre-approved by a customer
   * @param {string} customerId - The customer's UUID
   * @param {string} salesRepId - The sales rep's UUID
   * @returns {Promise<boolean>} True if pre-approved, false otherwise
   */
  async isRepPreApproved(customerId, salesRepId) {
    try {
      const { data, error } = await supabase
        .from('pre_approved_reps')
        .select('id')
        .eq('customer_id', customerId)
        .eq('sales_rep_id', salesRepId)
        .maybeSingle();

      if (error) throw error;

      return !!data;
    } catch (error) {
      logger.error('PreApprovedReps: Error checking pre-approval', { error: error.message, customerId, salesRepId });
      return false;
    }
  },

  /**
   * Add a sales rep to customer's pre-approved list
   * @param {string} customerId - The customer's UUID
   * @param {string} salesRepId - The sales rep's UUID
   * @param {string} notes - Optional notes
   * @returns {Promise<Object|null>} Created pre-approval record or null
   */
  async addPreApprovedRep(customerId, salesRepId, notes = null) {
    try {
      logger.debug('PreApprovedReps: Adding pre-approval', { customerId, salesRepId });

      const { data, error } = await supabase
        .from('pre_approved_reps')
        .insert([{
          customer_id: customerId,
          sales_rep_id: salesRepId,
          approved_by: customerId,
          notes: notes
        }])
        .select()
        .single();

      if (error) {
        // Handle unique constraint violation (already pre-approved)
        if (error.code === '23505') {
          logger.warn('Sales rep already pre-approved', { customerId, salesRepId });
          return { alreadyExists: true };
        }
        throw error;
      }

      logger.debug('PreApprovedReps: Successfully added pre-approval', { customerId, salesRepId });
      return data;
    } catch (error) {
      logger.error('PreApprovedReps: Error adding pre-approval', { error: error.message, customerId, salesRepId });
      return null;
    }
  },

  /**
   * Remove a sales rep from customer's pre-approved list
   * @param {string} customerId - The customer's UUID
   * @param {string} salesRepId - The sales rep's UUID
   * @returns {Promise<boolean>} Success status
   */
  async removePreApprovedRep(customerId, salesRepId) {
    try {
      logger.debug('PreApprovedReps: Removing pre-approval', { customerId, salesRepId });

      const { error } = await supabase
        .from('pre_approved_reps')
        .delete()
        .eq('customer_id', customerId)
        .eq('sales_rep_id', salesRepId);

      if (error) throw error;

      logger.debug('PreApprovedReps: Successfully removed pre-approval', { customerId, salesRepId });
      return true;
    } catch (error) {
      logger.error('PreApprovedReps: Error removing pre-approval', { error: error.message, customerId, salesRepId });
      return false;
    }
  },

  /**
   * Get count of customers who pre-approved a sales rep
   * @param {string} salesRepId - The sales rep's UUID
   * @returns {Promise<number>} Count of pre-approvals
   */
  async getPreApprovalCount(salesRepId) {
    try {
      const { count, error } = await supabase
        .from('pre_approved_reps')
        .select('*', { count: 'exact', head: true })
        .eq('sales_rep_id', salesRepId);

      if (error) throw error;

      return count || 0;
    } catch (error) {
      logger.error('Error getting pre-approval count', { error: error.message, salesRepId });
      return 0;
    }
  },

  /**
   * Search for sales reps by name, email, or company
   * @param {string} searchQuery - Search term to find sales reps
   * @returns {Promise<Array>} Array of matching sales rep objects
   */
  async searchSalesReps(searchQuery) {
    try {
      logger.debug('PreApprovedReps: Searching sales reps', { searchQuery });

      if (!searchQuery || searchQuery.trim().length === 0) {
        // If no search query, return all sales reps (up to 50)
        const { data, error } = await supabase
          .from('user_profiles')
          .select('id, email, first_name, last_name, company, phone, user_type')
          .eq('user_type', 'sales')
          .order('first_name', { ascending: true })
          .limit(50);

        if (error) throw error;

        return (data || []).map(rep => ({
          id: rep.id,
          name: `${rep.first_name || ''} ${rep.last_name || ''}`.trim() || 'Unknown',
          email: rep.email || '',
          company: rep.company || 'Unknown Company',
          phone: rep.phone || ''
        }));
      }

      // Search by name, email, or company (case insensitive)
      const searchTerm = searchQuery.trim();

      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, email, first_name, last_name, company, phone, user_type')
        .eq('user_type', 'sales')
        .or(`first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%,company.ilike.%${searchTerm}%`)
        .order('first_name', { ascending: true })
        .limit(50);

      if (error) throw error;

      logger.debug('PreApprovedReps: Found sales reps', { count: data?.length || 0 });

      return (data || []).map(rep => ({
        id: rep.id,
        name: `${rep.first_name || ''} ${rep.last_name || ''}`.trim() || 'Unknown',
        email: rep.email || '',
        company: rep.company || 'Unknown Company',
        phone: rep.phone || ''
      }));
    } catch (error) {
      logger.error('PreApprovedReps: Error searching sales reps', { error: error.message, searchQuery });
      return [];
    }
  },

  /**
   * Add a pre-approved rep by looking up their email address
   * @param {string} customerId - The customer's UUID
   * @param {string} salesRepEmail - The sales rep's email address
   * @returns {Promise<Object|null>} The added pre-approval record or null
   */
  async addPreApprovedRepByEmail(customerId, salesRepEmail) {
    try {
      logger.debug('PreApprovedReps: Looking up sales rep by email', { salesRepEmail });

      // Find the sales rep by email (use maybeSingle to avoid error when not found)
      const { data: salesRep, error: lookupError } = await supabase
        .from('user_profiles')
        .select('id, email, first_name, last_name, company, user_type')
        .eq('email', salesRepEmail.toLowerCase())
        .eq('user_type', 'sales')
        .maybeSingle();

      if (lookupError) {
        logger.error('PreApprovedReps: Error looking up sales rep', { error: lookupError.message, salesRepEmail });
        return null;
      }

      if (!salesRep) {
        logger.warn('PreApprovedReps: Sales rep not found', { salesRepEmail });
        return null;
      }

      logger.debug('PreApprovedReps: Found sales rep', { salesRepId: salesRep.id, salesRepEmail });

      // Add to pre-approved list
      const result = await this.addPreApprovedRep(customerId, salesRep.id);

      if (result) {
        return {
          ...result,
          salesRepId: salesRep.id,
          salesRepEmail: salesRep.email,
          salesRepName: `${salesRep.first_name || ''} ${salesRep.last_name || ''}`.trim(),
          salesRepCompany: salesRep.company
        };
      }

      return null;
    } catch (error) {
      logger.error('PreApprovedReps: Error adding by email', { error: error.message, salesRepEmail });
      return null;
    }
  }
};

export default preApprovedRepsService;
